import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

/* Turn a YouTube / Vimeo watch URL into an embeddable one. Returns null
   for anything we can't confidently embed (rendered as a plain link). */
function toEmbedUrl(rawUrl) {
  if (!rawUrl) return null
  let url
  try {
    url = new URL(rawUrl.trim())
  } catch {
    return null
  }
  const host = url.hostname.replace(/^www\./, '')

  // YouTube
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1)
    return id ? `https://www.youtube.com/embed/${id}` : null
  }
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (url.pathname === '/watch') {
      const id = url.searchParams.get('v')
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
    if (url.pathname.startsWith('/embed/')) return url.href
    if (url.pathname.startsWith('/shorts/')) {
      const id = url.pathname.split('/')[2]
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
  }

  // Vimeo
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0]
    return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null
  }
  if (host === 'player.vimeo.com') return url.href

  return null
}

function isDirectVideoFile(rawUrl) {
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(rawUrl ?? '')
}

const CATEGORY_ORDER = ['register', 'payment', 'general']

function VideoCard({ video }) {
  const embedUrl = toEmbedUrl(video.url)
  const directFile = !embedUrl && isDirectVideoFile(video.url)

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
      <div className="aspect-video bg-gray-900">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        ) : directFile ? (
          <video src={video.url} controls className="w-full h-full" />
        ) : (
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full h-full items-center justify-center text-sm font-medium text-white/90 hover:text-white bg-gray-900"
          >
            ▶ {video.title}
          </a>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-base font-semibold text-gray-900">{video.title}</h3>
        {video.description && (
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">{video.description}</p>
        )}
      </div>
    </div>
  )
}

export default function Help() {
  const { t } = useTranslation()
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('help_videos')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
      setVideos(data ?? [])
      setLoading(false)
    }
    load()
  }, [])

  const categoryTitle = (cat) => ({
    register: t('help.categoryRegister'),
    payment: t('help.categoryPayment'),
    general: t('help.categoryGeneral'),
  }[cat] ?? t('help.categoryGeneral'))

  // Group by category, preserving the preferred category order and
  // appending any unexpected categories at the end.
  const grouped = videos.reduce((acc, v) => {
    const key = v.category || 'general'
    ;(acc[key] ??= []).push(v)
    return acc
  }, {})
  const categories = [
    ...CATEGORY_ORDER.filter(c => grouped[c]?.length),
    ...Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)),
  ]

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">{t('help.title')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('help.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : videos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-sm text-gray-400">
          {t('help.empty')}
        </div>
      ) : (
        <div className="space-y-12">
          {categories.map(cat => (
            <section key={cat} className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900">{categoryTitle(cat)}</h2>
              <div className="grid gap-6 sm:grid-cols-2">
                {grouped[cat].map(v => <VideoCard key={v.id} video={v} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
